import React, { useState, useEffect } from 'react';
import { useTimeTracking } from '../context/TimeTrackingContext';
import { timeTrackingApi } from '../services/timeTrackingApi';
import IdleTimeDisplay from '../components/IdleTimeDisplay';
import OfflineSyncDebug from '../components/OfflineSyncDebug';

const TimeTrackingPage = () => {
  const { isTracking } = useTimeTracking();
  const [dailySummary, setDailySummary] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [projects, setProjects] = useState([]);
  const [timeReports, setTimeReports] = useState([]);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    loadSessions();
    loadDailySummary();
    loadProjects();
    loadTimeReports();
  }, [selectedDate]);

  const loadSessions = async () => {
    try {
      const response = await timeTrackingApi.getSessionsByDate(selectedDate);
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    }
  };

  const loadDailySummary = async () => {
    try {
      const response = await timeTrackingApi.getDailySummary(selectedDate);
      setDailySummary(response.data || []);
    } catch (error) {
      console.error('Failed to load daily summary:', error);
    }
  };
  
  const loadProjects = async () => {
    try {
      const response = await fetch('/api/projects', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setProjects(data || []);
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };
  
  const loadTimeReports = async () => {
    try {
      const response = await timeTrackingApi.getSessionsByDate(selectedDate);
      const sessionData = response.data.sessions || [];
      
      // Group by project and task
      const grouped = sessionData.reduce((acc, session) => {
        const key = `${session.project_id || 'No Project'}-${session.task_id || 'No Task'}`;
        if (!acc[key]) {
          acc[key] = {
            project_id: session.project_id,
            task_id: session.task_id,
            project_name: session.project_name,
            task_title: session.task_title,
            total_seconds: 0,
            active_seconds: 0,
            idle_seconds: 0,
            sessions: [],
            screenshots: []
          };
        }
        acc[key].total_seconds += session.total_seconds || 0;
        acc[key].active_seconds += session.active_seconds || 0;
        acc[key].idle_seconds += session.idle_seconds || 0;
        acc[key].sessions.push(session);
        if (session.screenshots) {
          acc[key].screenshots.push(...session.screenshots);
        }
        return acc;
      }, {});
      setTimeReports(Object.values(grouped));
    } catch (error) {
      console.error('Failed to load time reports:', error);
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}h ${mins}m ${secs}s`;
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Time Reports</h1>
        {isTracking && (
          <div className="text-sm text-green-600 flex items-center">
            <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
            Currently tracking time
          </div>
        )}
      </div>
      
      {/* Date Filter */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <label className="block text-sm font-medium mb-2">Select Date:</label>
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="border rounded px-3 py-2"
        />
      </div>

      {/* Daily Summary */}
      {dailySummary.length > 0 && (
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <h3 className="text-lg font-semibold mb-4">Daily Summary</h3>
          {Array.isArray(dailySummary) ? dailySummary.map((summary, index) => (
            <div key={index} className="border-b pb-2 mb-2 last:border-b-0">
              <div className="grid grid-cols-4 gap-4 text-sm">
                <div>Total: {formatTime(summary.total_seconds)}</div>
                <div>Active: {formatTime(summary.active_seconds)}</div>
                <div>Idle: {formatTime(summary.idle_seconds)}</div>
                <div>Screenshots: {summary.screenshots_count}</div>
              </div>
            </div>
          )) : (
            <div className="border-b pb-2 mb-2">
              <div className="grid grid-cols-4 gap-4 text-sm">
                <div>Total: {formatTime(dailySummary.total_seconds || 0)}</div>
                <div>Active: {formatTime(dailySummary.active_seconds || 0)}</div>
                <div>Idle: {formatTime(dailySummary.idle_seconds || 0)}</div>
                <div>Screenshots: {dailySummary.screenshots_count || 0}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Project & Task Wise Reports */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <h3 className="text-lg font-semibold mb-4">Project & Task Wise Time Reports</h3>
        {timeReports.length === 0 ? (
          <p className="text-gray-500">No time reports found for selected date</p>
        ) : (
          <div className="space-y-4">
            {timeReports.map((report, index) => {
              return (
                <div key={index} className="border rounded p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="font-medium text-lg">
                        {report.project_name || 'No Project'} - {report.task_title || `Task #${report.task_id}` || 'No Task'}
                      </h4>
                      <div className="grid grid-cols-3 gap-4 mt-2 text-sm">
                        <div>Total Time: <span className="font-medium">{formatTime(report.total_seconds)}</span></div>
                        <div>Active Time: <span className="font-medium text-green-600">{formatTime(report.active_seconds)}</span></div>
                        <div>Idle Time: <span className="font-medium text-red-600">{formatTime(report.idle_seconds)}</span></div>
                        <div>Screenshots: <span className="font-medium">{report.screenshots.length}</span></div>
                        <div>Productivity: <span className="font-medium text-blue-600">
                          {report.total_seconds > 0 ? Math.round((report.active_seconds / report.total_seconds) * 100) : 0}%
                        </span></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Sessions for this project/task */}
                  <div className="mt-3">
                    <h5 className="text-sm font-medium mb-2">Sessions ({report.sessions.length})</h5>
                    <div className="space-y-1">
                      {report.sessions.map((session) => (
                        <div key={session.id} className="text-xs bg-gray-50 p-2 rounded flex justify-between">
                          <span>
                            {new Date(session.start_time).toLocaleTimeString()} - 
                            {session.end_time ? new Date(session.end_time).toLocaleTimeString() : 'Active'}
                          </span>
                          <span>{formatTime(session.total_seconds)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Screenshots */}
                  {report.screenshots.length > 0 && (
                    <div className="mt-3">
                      <h5 className="text-sm font-medium mb-2">Screenshots</h5>
                      <div className="grid grid-cols-6 gap-2">
                        {report.screenshots.slice(0, 6).map((screenshot, idx) => (
                          <div key={idx} className="text-xs bg-blue-50 p-2 rounded text-center">
                            📸 {new Date(screenshot.taken_at).toLocaleTimeString()}
                          </div>
                        ))}
                        {report.screenshots.length > 6 && (
                          <div className="text-xs bg-gray-100 p-2 rounded text-center">
                            +{report.screenshots.length - 6} more
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* All Sessions List */}
      <div className="bg-white p-4 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">All Sessions</h3>
        {sessions.length === 0 ? (
          <p className="text-gray-500">No sessions found for selected date</p>
        ) : (
          <div className="space-y-2">
            {sessions.map((session) => (
              <div key={session.id} className="border rounded p-3">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium">
                      {new Date(session.start_time).toLocaleTimeString()} - 
                      {session.end_time ? new Date(session.end_time).toLocaleTimeString() : 'Active'}
                    </p>
                    <p className="text-sm text-gray-600">
                      Project: {session.project_name || 'N/A'} | 
                      Task: {session.task_title || `#${session.task_id}` || 'N/A'}
                    </p>
                    <p className="text-sm text-gray-600">
                      Total: {formatTime(session.total_seconds)} | 
                      Active: {formatTime(session.active_seconds)} | 
                      Idle: {formatTime(session.idle_seconds)}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs ${
                    session.status === 'active' ? 'bg-green-100 text-green-800' :
                    session.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {session.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Offline Sync Debug Component */}
      <OfflineSyncDebug />
    </div>
  );
};

export default TimeTrackingPage;